import type { DataTableColumn, SortCriterion, TableLayout } from './types'

export const MAX_SORT = 5

// "legalName,-createdAtUtc" (the `sort` of the APIs: comma-separated keys, '-' = descending).
// With `columns`, only the criteria of columns that still exist and sort are sent.
export function sortParam(
  sort: readonly SortCriterion[],
  columns?: readonly Pick<DataTableColumn, 'key' | 'sortKey' | 'sortable'>[],
): string | undefined {
  const criteria = columns ? validSort(sort, columns) : sort
  const keyOf = (key: string) => columns?.find((column) => column.key === key)?.sortKey ?? key
  const text = criteria.map((criterion) => `${criterion.desc ? '-' : ''}${keyOf(criterion.key)}`).join(',')
  return text || undefined
}

// The sort kept in the session may name a column that was removed or no longer sorts (another
// version of the screen): those criteria are dropped, as are repeated ones.
export function validSort(sort: readonly SortCriterion[], columns: readonly Pick<DataTableColumn, 'key' | 'sortable'>[]): SortCriterion[] {
  const seen = new Set<string>()
  return sort
    .filter((criterion) => {
      const column = columns.find((candidate) => candidate.key === criterion.key)
      if (!column || column.sortable === false || seen.has(criterion.key)) return false
      seen.add(criterion.key)
      return true
    })
    .slice(0, MAX_SORT)
}

export const sameSort = (a: readonly SortCriterion[], b: readonly SortCriterion[]) =>
  a.length === b.length && a.every((criterion, i) => criterion.key === b[i]?.key && criterion.desc === b[i]?.desc)

// The saved layout, if it is for this version of the table's columns.
export function layoutFor(layout: TableLayout | undefined, version: number): TableLayout | undefined {
  return layout && (layout.version ?? 1) === version ? layout : undefined
}

// Header click. Alone: sort only by that column, ascending → descending → no order.
// With Shift (`add`): the column joins the sort as the next criterion (or flips/leaves it if it's there).
export function toggleSort(sort: readonly SortCriterion[], key: string, add: boolean): SortCriterion[] {
  const index = sort.findIndex((criterion) => criterion.key === key)
  const current = sort[index]
  if (add) {
    if (!current) return [...sort, { key, desc: false }].slice(0, MAX_SORT)
    if (!current.desc) return sort.map((criterion, i) => (i === index ? { key, desc: true } : criterion))
    return sort.filter((_, i) => i !== index)
  }
  if (sort.length === 1 && current) return current.desc ? [] : [{ key, desc: true }]
  return [{ key, desc: false }]
}

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true })

function comparable(value: unknown): number | string | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return Number.isNaN(value) ? null : value
  if (typeof value === 'boolean') return value ? 1 : 0
  return String(value)
}

// Client sort (lists the screen already has whole). Empty values always go last; ties keep the original order.
export function sortRows<Row>(rows: readonly Row[], sort: readonly SortCriterion[], columns: readonly DataTableColumn<Row>[]): Row[] {
  const criteria = sort
    .map((criterion) => ({ criterion, column: columns.find((column) => column.key === criterion.key) }))
    .filter((item): item is { criterion: SortCriterion; column: DataTableColumn<Row> } => !!item.column && item.column.sortable !== false)
  if (!criteria.length) return [...rows]
  const valueOf = (column: DataTableColumn<Row>, row: Row) =>
    comparable(column.sortValue ? column.sortValue(row) : (row as Record<string, unknown>)[column.key])
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      for (const { criterion, column } of criteria) {
        const x = valueOf(column, a.row)
        const y = valueOf(column, b.row)
        if (x === y) continue
        if (x === null) return 1
        if (y === null) return -1
        const result = typeof x === 'number' && typeof y === 'number' ? x - y : collator.compare(String(x), String(y))
        if (result !== 0) return criterion.desc ? -result : result
      }
      return a.index - b.index
    })
    .map((item) => item.row)
}

// Columns in the person's order, new columns (not in the saved order) at their default place.
export function orderedColumns<Row>(columns: readonly DataTableColumn<Row>[], layout: TableLayout | undefined): DataTableColumn<Row>[] {
  if (!layout?.order.length) return [...columns]
  const byKey = new Map(columns.map((column) => [column.key, column]))
  const result = layout.order.map((key) => byKey.get(key)).filter((column): column is DataTableColumn<Row> => !!column)
  columns.forEach((column, defaultIndex) => {
    if (result.includes(column)) return
    // After the column that precedes it by default, or at the start.
    const previous = columns.slice(0, defaultIndex).reverse().find((candidate) => result.includes(candidate))
    result.splice(previous ? result.indexOf(previous) + 1 : 0, 0, column)
  })
  return result
}

export function isHidden(column: Pick<DataTableColumn, 'key' | 'hideable' | 'defaultHidden'>, layout: TableLayout | undefined): boolean {
  if (column.hideable === false) return false
  if (layout && (layout.order.includes(column.key) || layout.hidden.includes(column.key))) return layout.hidden.includes(column.key)
  return !!column.defaultHidden
}

// Layout to save from the panel's state (every column, so a column added later keeps its default).
// Columns of the previous layout the table doesn't show now (e.g. by permission, or a filter) keep
// their place and their hidden state, so they come back as they were.
export function layoutOf(
  columns: readonly Pick<DataTableColumn, 'key'>[],
  hidden: ReadonlySet<string>,
  version = 1,
  previous?: TableLayout,
): TableLayout {
  const order = columns.map((column) => column.key)
  const hiddenKeys = order.filter((key) => hidden.has(key))
  if (previous) {
    previous.order.forEach((key, index) => {
      if (order.includes(key)) return
      const before = previous.order.slice(0, index).reverse().find((candidate) => order.includes(candidate))
      order.splice(before ? order.indexOf(before) + 1 : 0, 0, key)
    })
    previous.hidden.forEach((key) => {
      if (!columns.some((column) => column.key === key) && !hiddenKeys.includes(key)) hiddenKeys.push(key)
    })
  }
  // The API keeps up to 60 keys: past that, the columns that no longer exist are let go.
  if (order.length > MAX_LAYOUT_KEYS) return layoutOf(columns, hidden, version)
  return { order, hidden: hiddenKeys, version }
}

const MAX_LAYOUT_KEYS = 60

export function moveKey(keys: readonly string[], key: string, toIndex: number): string[] {
  const from = keys.indexOf(key)
  if (from < 0) return [...keys]
  const result = keys.filter((item) => item !== key)
  result.splice(Math.max(0, Math.min(toIndex, result.length)), 0, key)
  return result
}

// Sort of the session (sessionStorage): survives reloads and navigation, not other tabs/browsers.
const SESSION_PREFIX = 'aubaine.table-sort'

export function readSessionSort(scope: string, tableId: string): SortCriterion[] | undefined {
  try {
    const raw = sessionStorage.getItem(`${SESSION_PREFIX}:${scope}:${tableId}`)
    if (!raw) return undefined
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return undefined
    return parsed
      .filter((item): item is SortCriterion => typeof item?.key === 'string' && typeof item?.desc === 'boolean')
      .slice(0, MAX_SORT)
  } catch {
    return undefined
  }
}

export function writeSessionSort(scope: string, tableId: string, sort: readonly SortCriterion[]) {
  try {
    sessionStorage.setItem(`${SESSION_PREFIX}:${scope}:${tableId}`, JSON.stringify(sort))
  } catch {
    // Private mode or storage blocked: the sort just doesn't survive a reload.
  }
}
